import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { AccountService } from '../../../../../services/account/account.service';
import { CustomerService } from '../../../../../services/customer/customer.service';
import { Customer } from '../../../../../models/customer';
import { dateStringFromISO8601, timeStringFromISO8601 } from '../../../../../utils';
import { OrganizationConfigService } from '../../../../../services/organization-config/organization-config.service';
import { InboxService } from '../../../../../services/inbox/inbox.service';

@Component({
  selector: 'app-merge-customers',
  templateUrl: './merge-customers.component.html',
  styleUrls: ['./merge-customers.component.css']
})
export class MergeCustomersComponent implements OnInit {

  public primaryCustomerId: number = null;
  public secondaryCustomerId: number = null;

  public primaryCustomer: Customer = null;
  public secondaryCustomer: Customer = null;

  public loadingMerge = false;
  public loadingPrimaryCustomer = false;
  public loadingSecondaryCustomer = false;
  public primaryCustomerNotFound = false;
  public secondaryCustomerNotFound = false;

  public confirmationChecked = false;

  public mergedCustomerId: number = null;

  public mergeErrorMessage = '';
  public serverError: number = null;

  public readonly dateStringFromISO8601 = dateStringFromISO8601;
  public readonly timeStringFromISO8601 = timeStringFromISO8601;

  constructor(
    public accountService: AccountService,
    public orgConfig: OrganizationConfigService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private customerService: CustomerService,
    private inboxService: InboxService,
  ) {}

  ngOnInit() {
    if (!this.accountService.getMyAccount().can_manage_members) {
      return;
    }

    const initialQPs = this.activatedRoute.snapshot.queryParamMap;
    const inputPrimaryId = parseInt(initialQPs.get('primaryId'), 10);
    const inputSecondaryId = parseInt(initialQPs.get('secondaryId'), 10);
    if (!isNaN(inputPrimaryId)) {
      this.primaryCustomerId = inputPrimaryId;
      this.loadPrimaryCustomer();
    }
    if (!isNaN(inputSecondaryId)) {
      this.secondaryCustomerId = inputSecondaryId;
      this.loadSecondaryCustomer();
    }
  }

  /**
   * Load the primary customer.
   */
  public loadPrimaryCustomer() {
    if (!this.primaryCustomerId) {
      this.primaryCustomerNotFound = true;
      return;
    }

    this.loadingPrimaryCustomer = true;
    this.primaryCustomerNotFound = false;

    this.customerService
      .getCustomer(this.primaryCustomerId)
      .then(customer => {
        this.primaryCustomer = customer;
        this.addCustomersToGetParams();
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.primaryCustomerNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingPrimaryCustomer = false; });
  }

  /**
   * Load the secondary customer.
   */
  public loadSecondaryCustomer() {
    if (!this.secondaryCustomerId) {
      this.secondaryCustomerNotFound = true;
      return;
    }

    this.loadingSecondaryCustomer = true;
    this.secondaryCustomerNotFound = false;

    this.customerService
      .getCustomer(this.secondaryCustomerId)
      .then(customer => {
        this.secondaryCustomer = customer;
        this.addCustomersToGetParams();
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.secondaryCustomerNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingSecondaryCustomer = false; });
  }

  public resetPrimaryCustomer() {
    this.primaryCustomer = null;
    this.loadingPrimaryCustomer = false;
    this.primaryCustomerNotFound = false;
    this.confirmationChecked = false;
    this.mergedCustomerId = null;
    this.mergeErrorMessage = '';
    this.addCustomersToGetParams();
  }

  public resetSecondaryCustomer() {
    this.secondaryCustomer = null;
    this.loadingSecondaryCustomer = false;
    this.secondaryCustomerNotFound = false;
    this.confirmationChecked = false;
    this.mergedCustomerId = null;
    this.mergeErrorMessage = '';
    this.addCustomersToGetParams();
  }

  public swapCustomers() {
    const oldSecondaryId = this.secondaryCustomerId;
    this.secondaryCustomerId = this.primaryCustomerId;
    this.primaryCustomerId = oldSecondaryId;

    this.resetPrimaryCustomer();
    this.resetSecondaryCustomer();
    if (this.primaryCustomerId) {
      this.loadPrimaryCustomer();
    }
    if (this.secondaryCustomerId) {
      this.loadSecondaryCustomer();
    }
  }

  public submitMerge() {
    this.mergeErrorMessage = '';

    this.loadingMerge = true;

    this.customerService
      .mergeCustomers(this.primaryCustomer.id, this.secondaryCustomer.id)
      .then(() => {
        this.mergedCustomerId = this.primaryCustomer.id;
        this.primaryCustomerId = null;
        this.secondaryCustomerId = null;
        this.primaryCustomer = null;
        this.secondaryCustomer = null;
        this.inboxService.loadInboxCounts(true);
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.mergeErrorMessage = 'You do not have permission to merge customers.';
        } else if (reason.status === 400) {
          this.mergeErrorMessage = reason.error;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingMerge = false; });
  }

  private addCustomersToGetParams() {
    const filtersToAdd: any = {};
    if (this.primaryCustomer) {
      filtersToAdd.primaryId = this.primaryCustomer.id;
    }
    if (this.secondaryCustomer) {
      filtersToAdd.secondaryId = this.secondaryCustomer.id;
    }

    this.router.navigate(
      [],
      {
        relativeTo: this.activatedRoute,
        queryParams: filtersToAdd,
        replaceUrl: true
      }
    );
  }
}
